import { loadSceneFromAPI } from ".";

import type { FileId } from "@excalidraw/element/types";
import type { BinaryFileData } from "@excalidraw/excalidraw/types";

// Whiteboard image storage backed by the OrganiseWise Azure-blob endpoints,
// replacing Firebase (which the self-host can't write to). Images are uploaded
// once to blob storage; the returned public URL is then stored as the element's
// `dataURL` (Excalidraw renders it directly via `new Image().src`), so the scene
// JSON stays tiny and images survive reloads/shares.

const getBaseUrl = () =>
  (import.meta.env as any).VITE_APP_ORGANISEWISE_API_BASE_URL ||
  "https://prod.backend.organisewise.me";

// A blob/public URL we've already persisted — don't re-upload these.
const isRemoteUrl = (dataURL: string) =>
  /^https?:\/\//i.test(dataURL);

const dataURLToBlob = async (dataURL: string): Promise<Blob> => {
  const res = await fetch(dataURL);
  return res.blob();
};

/**
 * Uploads a single image file to Azure blob storage (scoped to the board) and
 * returns its public URL. If the file's dataURL is already a remote URL, returns
 * it unchanged.
 */
export const uploadImageToBlob = async (
  documentId: string,
  file: BinaryFileData,
): Promise<string> => {
  if (isRemoteUrl(file.dataURL)) {
    return file.dataURL;
  }

  const blob = await dataURLToBlob(file.dataURL);
  const formData = new FormData();
  formData.append("file", blob, file.id);

  const response = await fetch(
    `${getBaseUrl()}/draw/excalidraw/${documentId}/image`,
    { method: "POST", body: formData },
  );

  if (!response.ok) {
    throw new Error(
      `Image upload failed with status ${response.status}: ${response.statusText}`,
    );
  }

  const data = await response.json();
  if (!data?.unique_url) {
    throw new Error("Image upload response missing unique_url");
  }
  return data.unique_url as string;
};

/**
 * Resolves image files by reading them from the board's persisted scene (the
 * backend stores each file with its public URL as `dataURL`). Used by the
 * FileManager on collab joins / reloads instead of fetching from Firebase.
 */
export const getImageFilesFromBackend = async (
  documentId: string,
  fileIds: FileId[],
): Promise<{
  loadedFiles: BinaryFileData[];
  erroredFiles: Map<FileId, true>;
}> => {
  const erroredFiles = new Map<FileId, true>();
  const loadedFiles: BinaryFileData[] = [];

  try {
    const scene = await loadSceneFromAPI(documentId);
    const files = (scene.files || {}) as Record<string, BinaryFileData>;
    for (const id of fileIds) {
      const file = files[id];
      if (file && file.dataURL) {
        loadedFiles.push(file);
      } else {
        erroredFiles.set(id, true);
      }
    }
  } catch {
    for (const id of fileIds) {
      erroredFiles.set(id, true);
    }
  }

  return { loadedFiles, erroredFiles };
};
